import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { lpForm } from 'lp-form'
import {
  useCommunity,
  serializeOptions,
  getCloudinaryUploadErrorMessage,
} from 'utils'

import {
  Field,
  FormSection,
  formValueSelector,
  propTypes as formPropTypes,
} from 'redux-form'
import {
  Input,
  InputLabel,
  InputError,
  SubmitButton,
  Textarea,
  Select,
} from 'lp-components'
import {
  ProfileMapDisplay,
  SectionBlock,
  SectionHeader,
  CloudinaryFileInput,
  CloudinaryMultiFileInput,
  RemovableThumbnail,
  RemovableFile,
  ToggleCheckbox,
} from 'components'
import { CLOUD_RESIZE_PRESET } from 'config'
import { camelCase } from 'lodash'

const propTypes = {
  ...formPropTypes,
  currentArtistLocation: PropTypes.oneOfType([
    Types.location,
    PropTypes.oneOf([null]),
  ]),
  flashErrorMessage: PropTypes.func.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  artistId: PropTypes.number.isRequired,
  demographics: PropTypes.arrayOf(Types.demographic).isRequired,
  badges: PropTypes.arrayOf(Types.badge),
  spotlightVideoUrl: PropTypes.string,
  profileCompletionRequired: PropTypes.bool,
}

const defaultProps = {
  profileCompletionRequired: false,
}

function profileCompletionLabel(baseLabel, profileCompletionRequired) {
  return profileCompletionRequired ? `${baseLabel}*` : baseLabel
}

const PRONOUN_OPTIONS = [
  'He/Him/His',
  'She/Her/Hers',
  'They/Them/Theirs',
  'Other',
]
const YEARS_OF_EXPERIENCE_OPTIONS = [
  { key: '1', value: 1 },
  { key: '2', value: 2 },
  { key: '3', value: 3 },
  { key: '4', value: 4 },
  { key: '5', value: 5 },
  { key: '6', value: 6 },
  { key: '7', value: 7 },
  { key: '8', value: 8 },
  { key: '9', value: 9 },
  { key: '10+', value: 10 },
]

const LogoLabel = ({ profileCompletionRequired, ...props }) => (
  <InputLabel {...props}>
    {profileCompletionLabel(
      'Profile Picture or Logo',
      profileCompletionRequired
    )}
    <span className="input-instructions">
      (Accepted formats include png, jpg and jpeg. Your file must be less than
      10 MB and will be cropped to be a square when uploaded)
    </span>
  </InputLabel>
)

const BGLogoLabel = ({ ...props }) => (
  <InputLabel {...props}>
    Background Check File
    <span className="input-instructions">
      Please note: Teaching Artists must submit a current background check to be
      approved for artlook®, please upload your file here. If a background check
      is not provided, you will not be approved for artlook®.
    </span>
  </InputLabel>
)

const SpotlightImagesLabel = ({ ...props }) => (
  <InputLabel {...props}>
    Spotlight Images
    <span className="input-instructions">
      (Accepted formats include png, jpg and jpeg. Your file must be less than
      10 MB)
    </span>
  </InputLabel>
)

const SpotlightVideoLabel = ({ ...props }) => (
  <InputLabel {...props}>
    Spotlight Video
    <span className="input-instructions">
      (Accepted formats include mp4. Your file must be less than 500 MB)
    </span>
  </InputLabel>
)

function AccountForm({
  handleSubmit,
  currentArtistLocation,
  submitting,
  change,
  flashErrorMessage,
  flashSuccessMessage,
  artistId,
  demographics,
  badges,
  spotlightVideoUrl,
  profileCompletionRequired,
}) {
  const [uploadError, setUploadError] = useState()
  const [uploadBGFileError, setUploadBGFileError] = useState()
  const [spotlightVideoError, setSpotlightVideoError] = useState()
  const uploadPath = `/artists/users/profile-pictures/${artistId}`
  const uploadBGFilePath = `/artists/users/background-check-files/${artistId}`
  const spotlightImagesUploadPath = `/artists/users/spotlight-images/${artistId}`
  const spotlightVideoUploadPath = `/artists/users/spotlight-video/${artistId}`

  const community = useCommunity()

  // Helper function to check if field should be visible
  const isFieldVisible = (community, fieldName) => {
    const fields = community?.teachingArtistRegister?.fields || {}
    return fields[fieldName]?.visible !== false // default to true if not specified
  }

  // Filter to only show badges visible in user portal
  const visibleBadges = (badges || []).filter(
    (badge) => badge.visibleInUserPortal
  )

  const photoVideoEnabled = community?.teachingArtist?.photoVideoEnabled

  // Helper function to filter demographics for racial categories only
  const filterRacialDemographics = (demographics) => {
    if (!demographics) return []

    // IDs of non-racial demographics to exclude
    const nonRacialIds = [7, 5, 6] // Special Education, Low Income, English Language Learners

    return demographics.filter(
      (demographic) => !nonRacialIds.includes(demographic.id)
    )
  }

  return (
    <form
      onSubmit={(values) => {
        setUploadError(null)
        setSpotlightVideoError(null)
        handleSubmit(values)
      }}
    >
      <SectionBlock data-cy="profile-details">
        <SectionHeader>
          <h2 id="account-details">Account Details</h2>
        </SectionHeader>
        <div className="account-details">
          <fieldset className="outer" aria-labelledby="account-details">
            <div className="row">
              <div className="col-3">
                <Field
                  name="profile.firstName"
                  component={Input}
                  label={profileCompletionLabel(
                    'First Name',
                    profileCompletionRequired
                  )}
                  autoComplete="given-name"
                />
              </div>
              <div className="col-3">
                <Field
                  name="profile.lastName"
                  component={Input}
                  label={profileCompletionLabel(
                    'Last Name',
                    profileCompletionRequired
                  )}
                  autoComplete="family-name"
                />
              </div>
              <div className="col-3">
                <Field
                  name="profile.pronouns"
                  placeholder="Select"
                  component={Select}
                  options={PRONOUN_OPTIONS}
                  label="Pronouns"
                  aria-label="Pronouns"
                />
              </div>
              <div className="col-3">
                <Field
                  name="demographicId"
                  placeholder="Select"
                  component={Select}
                  options={serializeOptions(
                    filterRacialDemographics(demographics),
                    {
                      key: 'displayName',
                      value: 'id',
                    }
                  )}
                  label="Racial Demographic"
                  aria-label="Racial Demographic"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-3">
                <Field
                  name="email"
                  component={Input}
                  label="Contact Email"
                  disabled
                  autoComplete="email"
                />
              </div>
              <div className="col-3">
                <Field
                  name="detail.phone"
                  component={Input}
                  label={profileCompletionLabel(
                    'Phone',
                    profileCompletionRequired
                  )}
                  autoComplete="tel"
                />
              </div>
              <div className="col-3">
                <Field
                  name="detail.website"
                  component={Input}
                  label="Website"
                  autoComplete="url"
                />
              </div>
              <div className="col-3">
                <Field name="socialMedia" component={Input} label="Instagram" />
              </div>
            </div>
            <div className="col-6">
              <div className="label-container">
                <label>Show phone on Map?</label>
                <Field
                  name="showPhoneOnMap"
                  component={ToggleCheckbox}
                  label={false}
                  className="toggle-checkbox"
                />
              </div>
              <div className="row">
                <div className="col-6">
                  <Field
                    name="profilePic"
                    accept=".png,.jpg,.jpeg"
                    labelComponent={(labelProps) => (
                      <LogoLabel
                        {...labelProps}
                        profileCompletionRequired={profileCompletionRequired}
                      />
                    )}
                    component={CloudinaryFileInput}
                    uploadPath={uploadPath}
                    uploadPreset={CLOUD_RESIZE_PRESET}
                    onUploadSuccess={({ secureUrl }) => {
                      setUploadError(null)
                      change('profilePic', secureUrl)
                      flashSuccessMessage(
                        'Profile picture successfully uploaded. Make sure to save your changes.'
                      )
                    }}
                    onUploadFailure={(err) => {
                      const message = getCloudinaryUploadErrorMessage(err)
                      setUploadError(message)
                      flashErrorMessage(message)
                    }}
                    previewComponent={RemovableThumbnail}
                    remove={() => {
                      setUploadError(null)
                      change('profilePic', '')
                      flashSuccessMessage(
                        'Existing profile picture successfully removed. Make sure to save your changes.'
                      )
                    }}
                    aria-describedby={uploadError ? 'upload-error' : undefined}
                  />
                  {uploadError && (
                    <InputError
                      touched
                      invalid
                      error={uploadError}
                      id="upload-error"
                    />
                  )}
                </div>
                {isFieldVisible(community, 'backgroundCheckFile') && (
                  <div className="col-6">
                    <Field
                      name="backgroundCheckFile"
                      labelComponent={BGLogoLabel}
                      previewComponent={RemovableFile}
                      component={CloudinaryFileInput}
                      uploadPath={uploadBGFilePath}
                      onUploadSuccess={() => {
                        setUploadBGFileError(null)
                        flashSuccessMessage(
                          'Background check file successfully uploaded. Make sure to save your changes.'
                        )
                      }}
                      onUploadFailure={(err) => {
                        const message = getCloudinaryUploadErrorMessage(err)
                        setUploadBGFileError(message)
                        flashErrorMessage(message)
                      }}
                      remove={() => {
                        setUploadBGFileError(null)
                        change('backgroundCheckFile', '')
                        flashSuccessMessage(
                          'Existing background check file successfully removed. Make sure to save your changes.'
                        )
                      }}
                      aria-describedby={
                        uploadBGFileError ? 'upload-bg-file-error' : undefined
                      }
                    />
                    {uploadBGFileError && (
                      <InputError
                        touched
                        invalid
                        error={uploadBGFileError}
                        id="upload-bg-file-error"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </fieldset>
        </div>
      </SectionBlock>
      {photoVideoEnabled && (
        <SectionBlock data-cy="photo-video-center">
          <SectionHeader>
            <h2 id="photo-video-center">Photo & Video Center</h2>
          </SectionHeader>
          <div className="row">
            <div className="col-6">
              <Field
                name="spotlightImages"
                component={CloudinaryMultiFileInput}
                labelComponent={SpotlightImagesLabel}
                uploadPath={spotlightImagesUploadPath}
                uploadPreset={CLOUD_RESIZE_PRESET}
                accept=".png,.jpg,.jpeg"
                onUploadSuccess={() =>
                  flashSuccessMessage(
                    'Spotlight image uploaded. Make sure to save your changes.'
                  )
                }
                onUploadFailure={flashErrorMessage}
                captionLabel={(filename) => `Add Image Caption for ${filename}`}
              />
            </div>
            <div className="col-6">
              <Field
                name="spotlightVideoUrl"
                labelComponent={SpotlightVideoLabel}
                accept=".mp4"
                component={CloudinaryFileInput}
                uploadPath={spotlightVideoUploadPath}
                onUploadSuccess={() => {
                  setSpotlightVideoError(null)
                  flashSuccessMessage(
                    'Spotlight video uploaded. Make sure to save your changes.'
                  )
                }}
                onUploadFailure={(err) => {
                  const message = getCloudinaryUploadErrorMessage(err)
                  setSpotlightVideoError(message)
                  flashErrorMessage(message)
                }}
                previewComponent={RemovableFile}
                remove={() => {
                  setSpotlightVideoError(null)
                  change('spotlightVideoUrl', '')
                  change('spotlightVideoCaption', '')
                  change('spotlightVideoMediaAlternative', '')
                  flashSuccessMessage(
                    'Spotlight video removed. Make sure to save your changes.'
                  )
                }}
                aria-describedby={
                  spotlightVideoError ? 'spotlight-video-error' : undefined
                }
              />
              {spotlightVideoError && (
                <InputError
                  touched
                  invalid
                  error={spotlightVideoError}
                  id="spotlight-video-error"
                />
              )}
              {spotlightVideoUrl && (
                <React.Fragment>
                  <Field
                    name="spotlightVideoCaption"
                    component={Input}
                    label="Add Video Caption"
                  />
                  <p
                    className="form-help-text"
                    id="spotlight-video-media-alt-help"
                  >
                    For accessibility (WCAG 2.1), provide a text description of
                    this video: important visuals, on-screen text, scene
                    changes, and anything not fully conveyed by audio or
                    captions. For a simple talking-head video where all meaning
                    is in the audio, you may state that briefly.
                  </p>
                  <Field
                    name="spotlightVideoMediaAlternative"
                    component={Textarea}
                    label="Text description of this video (accessibility)"
                    aria-describedby="spotlight-video-media-alt-help"
                  />
                </React.Fragment>
              )}
            </div>
          </div>
        </SectionBlock>
      )}
      <SectionBlock>
        <SectionHeader>
          <h2 id="location-details">Location</h2>
          <h3 className="subtitle">
            Your full address will not appear on the map. Your Show Page will
            only show your State and Zip Code.
          </h3>
          <div className="location-block">
            <div className="address-block">
              <fieldset
                className="address-form-block outer"
                aria-labelledby="location-details"
              >
                <FormSection name="address">
                  <Field
                    name="street"
                    component={Input}
                    label={profileCompletionLabel(
                      'Street Address',
                      profileCompletionRequired
                    )}
                    autoComplete="address-line1"
                  />
                  <div className="row">
                    <Field
                      className="col-5"
                      name="city"
                      component={Input}
                      label={profileCompletionLabel(
                        'City',
                        profileCompletionRequired
                      )}
                      autoComplete="address-level2"
                    />
                    <Field
                      className="col-3"
                      name="state"
                      component={Input}
                      label={profileCompletionLabel(
                        'State',
                        profileCompletionRequired
                      )}
                      autoComplete="address-level1"
                    />
                    <Field
                      className="col-4"
                      name="zip"
                      component={Input}
                      label={profileCompletionLabel(
                        'Zip Code',
                        profileCompletionRequired
                      )}
                      autoComplete="postal-code"
                    />
                  </div>
                </FormSection>
              </fieldset>
            </div>
            <div className="map-block">
              <ProfileMapDisplay
                location={currentArtistLocation}
                zoom={13}
                hidePopup
              />
            </div>
          </div>
        </SectionHeader>
      </SectionBlock>
      <SectionBlock>
        <SectionHeader>
          <h2 id="background-details">Background</h2>
          <div className="row">
            <Field
              className="col-3"
              name="profile.yearsOfExperience"
              component={Select}
              label={profileCompletionLabel(
                'Years of Experience',
                profileCompletionRequired
              )}
              options={serializeOptions(YEARS_OF_EXPERIENCE_OPTIONS, {
                key: 'key',
                value: 'value',
              })}
              placeholder="Select"
            />
            <Field
              className="col-3"
              name="resume"
              component={Input}
              label="Resume Link"
              autoComplete="url"
            />
            <Field
              className="col-3"
              name="portfolio"
              component={Input}
              label="Portfolio Link"
              autoComplete="url"
            />
            <Field
              className="col-3"
              name="idealWage"
              onInput={(e) => {
                // Remove any non-digit characters (including decimal points)
                e.target.value = e.target.value.replace(/[^0-9]/g, '')
              }}
              component={Input}
              label="Rate per Hour"
            />
          </div>
          <div className="row">
            <Field
              className="col-6"
              name="description"
              component={Textarea}
              label={profileCompletionLabel('Bio', profileCompletionRequired)}
            />
            <div className="col-6 background-block">
              {visibleBadges.map((badge) => (
                <div key={`badge-${badge.id || badge.name}`}>
                  <label>{badge.questionText}</label>
                  <Field
                    name={camelCase(badge.name)}
                    label={false}
                    component={ToggleCheckbox}
                    className="toggle-checkbox"
                  />
                </div>
              ))}
            </div>
          </div>
        </SectionHeader>
      </SectionBlock>
      <SubmitButton submitting={submitting}>Save</SubmitButton>
    </form>
  )
}

AccountForm.propTypes = propTypes
AccountForm.defaultProps = defaultProps

const selector = formValueSelector('artist-profile')

function mapStateToProps(state) {
  return {
    spotlightVideoUrl: selector(state, 'spotlightVideoUrl'),
  }
}

export default compose(
  connect(mapStateToProps),
  lpForm({
    name: 'artist-profile',
    constraints: {
      'profile.firstName': { presence: true },
      'profile.lastName': { presence: true },
      'detail.phone': {
        presence: true,
        format: {
          pattern: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
          message: 'must be a valid phone number',
        },
      },
      profilePic: { presence: true },
      'address.street': { presence: true },
      'address.city': { presence: true },
      'address.state': { presence: true },
      'address.zip': { presence: true },
      'profile.yearsOfExperience': { presence: true },
      description: { presence: true },
      spotlightVideoMediaAlternative: { length: { maximum: 15_000 } },
    },
    enableReinitialize: true,
  })
)(AccountForm)
